import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import { Monitor, FileText, Palette } from 'lucide-react';
import { SectionCard, FieldHelp, setField } from './SettingsUI';

const LAUNCHER_DESIGNS = [
  {
    id: 'ticket',
    labelKey: 'settings.customerInterface.styleTicket',
    blurbKey: 'settings.customerInterface.styleTicketBlurb',
  },
  {
    id: 'orb',
    labelKey: 'settings.customerInterface.styleOrb',
    blurbKey: 'settings.customerInterface.styleOrbBlurb',
  },
  {
    id: 'pill',
    labelKey: 'settings.customerInterface.stylePill',
    blurbKey: 'settings.customerInterface.stylePillBlurb',
  },
];

function normalizeLauncherStyle(raw) {
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  if (v === 'orb' || v === 'classic' || v === 'circle') return 'orb';
  if (v === 'pill' || v === 'dock' || v === 'capsule') return 'pill';
  return 'ticket';
}

const CustomerInterfaceTab = ({ form, setForm }) => {
  const { t } = useTranslation();
  const selectedStyle = normalizeLauncherStyle(form.widgetStyle);
  const brandColor = form.brandColor || '#022a6e';

  return (
    <div className="q2c-settings-compact">
      <div className="row g-3">
        <div className="col-lg-5">
          <SectionCard title={t('settings.customerInterface.buttonLabels')} icon={Monitor}>
            <FieldHelp
              label={t('settings.customerInterface.addToQuoteText')}
              help={t('settings.customerInterface.addToQuoteHelp')}
            >
              <Form.Control
                size="sm"
                value={form.addToQuoteButtonText || ''}
                onChange={(e) => setField(setForm, 'addToQuoteButtonText', e.target.value)}
                placeholder={t('settings.customerInterface.addToQuotePlaceholder')}
              />
            </FieldHelp>
            <FieldHelp
              label={t('settings.customerInterface.cartPageText')}
              help={t('settings.customerInterface.cartPageHelp')}
            >
              <Form.Control
                size="sm"
                value={form.cartPageButtonText || ''}
                onChange={(e) => setField(setForm, 'cartPageButtonText', e.target.value)}
                placeholder={t('settings.customerInterface.cartPagePlaceholder')}
              />
            </FieldHelp>
          </SectionCard>
        </div>

        <div className="col-lg-7">
          <SectionCard
            title={t('settings.customerInterface.brandAppearance')}
            icon={Palette}
            description={t('settings.customerInterface.brandAppearanceDesc')}
          >
            <div className="q2c-field-compact mb-2">
              <div className="q2c-field-compact-label">
                {t('settings.customerInterface.floatingButtonDesign')}
              </div>
              <div
                className="q2c-launcher-picker q2c-launcher-picker--compact"
                role="radiogroup"
                aria-label={t('settings.customerInterface.floatingButtonDesign')}
              >
                {LAUNCHER_DESIGNS.map((design) => {
                  const selected = selectedStyle === design.id;
                  return (
                    <button
                      key={design.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`q2c-launcher-pick${selected ? ' is-selected' : ''}`}
                      onClick={() => setField(setForm, 'widgetStyle', design.id)}
                    >
                      <div
                        className="q2c-launcher-pick-stage"
                        style={{ ['--pick-brand']: brandColor }}
                      >
                        <span
                          className={`q2c-launcher-preview q2c-launcher-preview--${design.id}`}
                          aria-hidden="true"
                        >
                          {design.id === 'ticket' ? (
                            <svg viewBox="0 0 24 24">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                              <line x1="8" y1="13" x2="16" y2="13" />
                              <line x1="8" y1="17" x2="16" y2="17" />
                            </svg>
                          ) : null}
                          {design.id === 'orb' ? (
                            <svg viewBox="0 0 24 24">
                              <path d="M7.5 11.5c-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3v-1c0-3.3-2-5.8-5.5-7.2" />
                              <path d="M16.5 11.5c-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3v-1c0-3.3-2-5.8-5.5-7.2" />
                            </svg>
                          ) : null}
                          {design.id === 'pill' ? (
                            <>
                              <svg viewBox="0 0 24 24">
                                <circle cx="9" cy="20" r="1.4" />
                                <circle cx="17" cy="20" r="1.4" />
                                <path d="M3 4h2l2.2 10.2a1.5 1.5 0 0 0 1.5 1.2h7.6a1.5 1.5 0 0 0 1.5-1.2L20 8H7" />
                              </svg>
                              <span>Quotes</span>
                            </>
                          ) : null}
                          <i className="q2c-launcher-preview-badge">3</i>
                        </span>
                      </div>
                      <strong>{t(design.labelKey)}</strong>
                      <p>{t(design.blurbKey)}</p>
                    </button>
                  );
                })}
              </div>
              <div className="form-text mt-1">
                {t('settings.customerInterface.floatingButtonHelp')}
              </div>
            </div>

            <div className="row g-2">
              <div className="col-md-6">
                <FieldHelp
                  label={t('settings.basicSetup.brandColor')}
                  help={t('settings.basicSetup.brandColorHelp')}
                >
                  <div className="q2c-setup-color">
                    <Form.Control
                      type="color"
                      value={brandColor}
                      onChange={(e) => setField(setForm, 'brandColor', e.target.value)}
                    />
                    <Form.Control
                      size="sm"
                      value={brandColor}
                      onChange={(e) => setField(setForm, 'brandColor', e.target.value)}
                      placeholder="#022a6e"
                    />
                  </div>
                </FieldHelp>
              </div>
              <div className="col-md-6">
                <FieldHelp
                  label={t('settings.basicSetup.hiddenPriceDisplay')}
                  help={t('settings.basicSetup.hiddenPriceDisplayHelp')}
                >
                  <Form.Control
                    size="sm"
                    value={form.hiddenPriceText || ''}
                    onChange={(e) => setField(setForm, 'hiddenPriceText', e.target.value)}
                    placeholder="N/A"
                  />
                </FieldHelp>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      <div className="mt-3">
        <SectionCard title={t('settings.customerInterface.messages')} icon={FileText}>
          <div className="row g-2">
            <div className="col-lg-4">
              <FieldHelp
                label={t('settings.customerInterface.emptyQuoteMessage')}
                help={t('settings.customerInterface.emptyQuoteHelp')}
              >
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.emptyQuoteMessage || ''}
                  onChange={(e) => setField(setForm, 'emptyQuoteMessage', e.target.value)}
                  placeholder={t('settings.customerInterface.emptyQuotePlaceholder')}
                />
              </FieldHelp>
            </div>
            <div className="col-lg-4">
              <FieldHelp
                label={t('settings.customerInterface.quoteSubmitted')}
                help={t('settings.customerInterface.quoteSubmittedHelp')}
              >
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.quoteSubmittedHeadline || ''}
                  onChange={(e) => setField(setForm, 'quoteSubmittedHeadline', e.target.value)}
                  placeholder={t('settings.customerInterface.quoteSubmittedPlaceholder')}
                />
              </FieldHelp>
            </div>
            <div className="col-lg-4">
              <FieldHelp
                label={t('settings.customerInterface.submitWidgetText')}
                help={t('settings.customerInterface.submitWidgetHelp')}
              >
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.quoteSubmittedBody || ''}
                  onChange={(e) => setField(setForm, 'quoteSubmittedBody', e.target.value)}
                  placeholder={
                    'Thank you for your interest! Your quote request has been successfully received.\nOur team will review your requirements and get back to you with a personalised quote within 24 hours.'
                  }
                />
              </FieldHelp>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default CustomerInterfaceTab;
export { normalizeLauncherStyle };
